import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { withStyles } from '@material-ui/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TablePagination from '@material-ui/core/TablePagination';
import Chip from '@material-ui/core/Chip';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import SectionHeader from '../../ReactHookForm/SectionHeader';
import CircularProgress from '@material-ui/core/CircularProgress';
import TextField from '@material-ui/core/TextField';
import debounce from 'lodash/debounce';
import classnames from 'classnames';
import TableSortLabel from '@material-ui/core/TableSortLabel';

const styles = theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  header: {
    padding: '16px 16px 0 16px',
    '& h6': {
      fontWeight: 500,
      color: '#495057',
    },
  },
  tableContainer: {
    maxHeight: 'calc(100vh - 300px)',
    overflowY: 'auto',
  },
  table: {
    minWidth: 650,
  },
  headerCell: {
    fontWeight: 600,
    backgroundColor: '#f8f9fa',
    color: '#495057',
  },
  row: {
    '&:hover': {
      backgroundColor: '#f8f9fa',
      cursor: 'pointer',
    },
  },
  chip: {
    borderRadius: 16,
    height: 24,
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  // Status chips (for the Status column)
  statusConnected: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
  },
  statusInProgress: {
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
  },
  statusConnecting: {
    backgroundColor: '#fff3e0',
    color: '#f57c00',
  },
  statusDisconnected: {
    backgroundColor: '#ffebee',
    color: '#c62828',
  },
  // Call status chips (for the Call Status column)
  callStatusHuman: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    border: '1px solid #a5d6a7',
  },
  callStatusRinging: {
    backgroundColor: '#fff3e0',
    color: '#f57c00',
    border: '1px solid #ffcc80',
  },
  callStatusVoicemail: {
    backgroundColor: '#fff3e0',
    color: '#f57c00',
    border: '1px solid #ffcc80',
  },
  callStatusDisconnected: {
    backgroundColor: '#fff',
    color: '#757575',
    border: '1px solid #bdbdbd',
  },
  pagination: {
    borderTop: '1px solid #e0e0e0',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px',
    width: '100%',
  },
  input: {
    background: 'transparent',
    borderWidth: 1,
    border: '1px solid #aaa',
    padding: 5,
    borderRadius: 3,
    // paddingBottom: 5,
    '&:focus': {
      outline: 'none',
    },
  },
  textInput: {
    width: '100%',
    fontSize: 18,
    color: '#777',
    padding: 10,
  },
});

const getStatusStyles = (type, value, classes) => {
  if (type === 'status') {
    // Normalize casing for comparison
    const lowerValue = typeof value === 'string' ? value.toLowerCase() : '';
    switch (lowerValue) {
      case 'queued':
        return classes.statusConnected; // Assuming Queued maps to a 'connected-like' state initially
      case 'in-progress':
      case 'answered': // Added: Map answered to in-progress style
        return classes.statusInProgress;
      case 'connecting':
      case 'ringing': // Added: Map ringing to connecting style
        return classes.statusConnecting;
      case 'disconnected':
      case 'ended': // Added: Map ended to disconnected style
      case 'completed': // Added: Map completed to disconnected style
        return classes.statusDisconnected;
      default:
        return ''; // No specific style for unknown statuses
    }
  } else if (type === 'callStatus') {
    switch (value) {
      case 'Human':
        return classes.callStatusHuman;
      case 'Ringing':
        return classes.callStatusRinging;
      case 'Voicemail':
        return classes.callStatusVoicemail;
      case 'Disconnected':
        return classes.callStatusDisconnected;
      default:
        return '';
    }
  } else if (type === 'outcome') {
    switch (value) {
      case true:
        return classes.callStatusHuman;
      case false:
      case null:
        return classes.statusDisconnected;
      default:
        return '';
    }
  }
  return '';
};

const getNestedValue = (obj, path) => {
  if (Array.isArray(path)) {
    return path.map(p => getNestedValue(obj, p)).join(' ');
  }
  return path.split('.').reduce((current, key) => (current ? current[key] : undefined), obj);
};

const CustomTable = withStyles(styles)(
  ({
    classes,
    columns,
    data,
    actionButtons,
    showExpandIcon = true,
    title,
    chipClasses = {},
    pagination = false,
    totalCount = 0,
    page = 0,
    pageSize = 10,
    onPageChange,
    onPageSizeChange,
    rowsPerPageOptions = [1, 5, 10, 25, 50],
    loading = false,
    allowRowClick = true,
    onSearch,
    allowSearch = true,
    onSort,
    sortColumn,
    sortDirection,
    headerActions,
  }) => {
    const history = useHistory();

    const handleRowClick = (event, row) => {
      // Don't navigate if clicking on an action button cell
      if (event.target.closest('td[data-action-cell="true"]') || !allowRowClick) {
        return;
      }
      history.push(`/call-manager/analysis/${row.orderLineId}`);
    };

    const renderCellContent = (column, row) => {
      const value = getNestedValue(row, column.id);

      if (column.type === 'status' || column.type === 'callStatus') {
        return (
          <Chip
            label={value}
            className={`${classes.chip} ${
              column.type === 'outcome' && chipClasses.outcome
                ? chipClasses.outcome[value]
                : getStatusStyles(column.type, value, classes)
            }`}
          />
        );
      } else if (column.type === 'outcome') {
        const formattedValue = (value === 'true' || value === true) ? true : false;
        return (
          <Chip
            label={(value === 'true' || value === true) ? 'Success' : 'Failed'}
            className={`${classes.chip} ${getStatusStyles(column.type, formattedValue, classes)}`}
          />
        );
      } else if (column.type === 'time') {
        const minutes = Math.floor(value / 60);
        const seconds = value % 60;
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      }

      if (column.type === 'date') {
        // Parse the UTC string and convert to local time
        const utcDate = new Date(`${value}Z`);
        let localDate = utcDate;

        if (column.id === 'startTime') {
          localDate = new Date(utcDate.getTime() - utcDate.getTimezoneOffset() * 60000);
        }

        return localDate.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      }

      return value;
    };

    const handleChangePage = (event, newPage) => {
      if (onPageChange) {
        onPageChange(newPage);
      }
    };

    const handleChangeRowsPerPage = event => {
      if (onPageSizeChange) {
        onPageSizeChange(parseInt(event.target.value, 10));
        pageSize = parseInt(event.target.value, 10);
      }
    };

    const debounceSearchHandler = useCallback(
      debounce(value => {
        onSearch(value);
      }, 500),
      [],
    );

    return (
      <div className={classes.container}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div className={classes.header}>
            <SectionHeader title={title} fontSize={24} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingRight: 24 }}>
            {headerActions}
            {allowSearch && (
              <input
                className={classnames(classes.input, classes.textInput, 'flex-1')}
                placeholder="Search"
                onChange={e => debounceSearchHandler(e.target.value)}
              />
            )}
          </div>
        </div>
        <TableContainer className={classes.tableContainer}>
          <Table className={classes.table} stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map(column => (
                  <TableCell
                    key={column.id}
                    className={classes.headerCell}
                    align={column.align || 'left'}
                    width={column.width}
                    sortDirection={sortColumn === column.sortId ? sortDirection : false}
                  >
                    {column.sortable ? (
                      <TableSortLabel
                        active={sortColumn === column.sortId}
                        direction={sortColumn === column.sortId ? sortDirection : 'asc'}
                        onClick={() => onSort && onSort(column.sortId)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
                {actionButtons && <TableCell className={classes.headerCell}>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columns.length + (actionButtons ? 1 : 0)} align="center">
                    <div className={classes.loadingContainer}>
                      <CircularProgress />
                    </div>
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length + (actionButtons ? 1 : 0)} align="center">
                    No data available
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row, rowIndex) => (
                  <TableRow
                    key={rowIndex}
                    className={allowRowClick && classes.row}
                    onClick={e => handleRowClick(e, row)}
                  >
                    {columns.map(column => (
                      <TableCell key={`${rowIndex}-${column.id}`} align={column.align || 'left'}>
                        {renderCellContent(column, row)}
                      </TableCell>
                    ))}
                    {actionButtons && (
                      <TableCell align="right" data-action-cell="true">
                        {actionButtons
                          .filter(x =>
                            title === 'Call Queue'
                              ? row.isActive === 'Ready to Call'
                                ? x.name === 'CallButton'
                                : row.isActive === 'ended'
                                ? x.name === 'ViewButton'
                                : null
                              : x,
                          )
                          .map((ActionButton, buttonIndex) => (
                            <ActionButton.component key={buttonIndex} row={row} />
                          ))}
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {pagination && (
          <TablePagination
            className={classes.pagination}
            component="div"
            count={totalCount}
            page={page}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            rowsPerPage={pageSize}
            rowsPerPageOptions={rowsPerPageOptions}
          />
        )}
      </div>
    );
  },
);

export default CustomTable;
